import { PageLayout } from "../components/page-layout"
import { TranslatedText } from "../components/translated-text"
import { useTranslation } from "../hooks/useTranslation"
import { useEffect, useState, useCallback } from 'react'
import { ResponsiveBankingLayout } from '@/components/responsive-banking-layout'
import { BankStatementUpload } from '@/components/bank-statement-upload'
import { BankAccountForm } from '@/components/bank-account-form'
import { BreadcrumbNavigation } from '@/components/breadcrumb-navigation'
import { useAuth } from '@/contexts/auth-context'
import { useToast } from '@/components/ui/use-toast'
import { bankingApi } from '@/lib/api/banking'
import { Button } from '@/components/ui/button'
import { Upload, Plus } from 'lucide-react'
import { getCompanyId } from '@/lib/config'

export default function BankingPage() {
  const { isAuthenticated, isLoading } = useAuth()
  const { toast } = useToast()
  const { tSync } = useTranslation()
  const [accountsRefreshKey, setAccountsRefreshKey] = useState(0)
  const [mounted, setMounted] = useState(false)
  const [companyId, setCompanyId] = useState<string>('')
  
  // Add Transaction Dialog State
  const [isAddTransactionOpen, setIsAddTransactionOpen] = useState(false)
  
  // Bank Statement Upload Dialog State
  const [isUploadStatementOpen, setIsUploadStatementOpen] = useState(false)
  
  // Bank Account Form Dialog State
  const [isBankAccountFormOpen, setIsBankAccountFormOpen] = useState(false)
  const [editingBankAccount, setEditingBankAccount] = useState<any>(null)
  const [transactionForm, setTransactionForm] = useState({
    bankAccountId: '',
    description: '',
    amount: '',
    transactionType: 'debit' as 'credit' | 'debit',
    transactionDate: new Date().toISOString().split('T')[0],
    merchantName: '',
    category: '',
    reference: '',
    memo: ''
  })
  const [transactionFormErrors, setTransactionFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    setMounted(true)
    
    // Use getCompanyId() to get the company ID from config (which reads from localStorage)
    // This ensures consistency with the header
    try {
      const currentCompanyId = getCompanyId()
      setCompanyId(currentCompanyId)
    } catch (error) {
      // Fallback to innovation-hub if getCompanyId fails
      const fallbackCompanyId = 'innovation-hub'
      setCompanyId(fallbackCompanyId)
    }
  }, [isAuthenticated, isLoading])

  // Listen for company changes from header
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const newCompanyId = getCompanyId()
        if (newCompanyId && newCompanyId !== companyId) {
          setCompanyId(newCompanyId)
        }
      } catch (error) {
        // Company ID not available yet
      }
    }

    // Listen for localStorage changes
    window.addEventListener('storage', handleStorageChange)
    
    // Also listen for custom events (in case localStorage doesn't trigger)
    const handleCompanyChange = (e: CustomEvent) => {
      const newCompanyId = e.detail.companyId
      if (newCompanyId && newCompanyId !== companyId) {
        setCompanyId(newCompanyId)
      }
    }
    
    window.addEventListener('companyChanged', handleCompanyChange as EventListener)

    // Periodic check to sync with header
    const interval = setInterval(() => {
      try {
        const currentCompanyId = getCompanyId()
        if (currentCompanyId && currentCompanyId !== companyId) {
          setCompanyId(currentCompanyId)
        }
      } catch (error) {
        // Company ID not available
      }
    }, 1000)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('companyChanged', handleCompanyChange as EventListener)
      clearInterval(interval)
    }
  }, [companyId])

  // Transaction creation functions - useCallback to prevent recreation
  const handleTransactionFormChange = useCallback((field: string, value: string) => {
    setTransactionForm(prev => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    setTransactionFormErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      }
      return prev
    })
  }, [])

  const validateTransactionForm = () => {
    const errors: Record<string, string> = {}
    
    if (!transactionForm.bankAccountId.trim()) {
      errors.bankAccountId = tSync('Bank account is required')
    }
    if (!transactionForm.description.trim()) {
      errors.description = tSync('Description is required')
    }
    if (!transactionForm.amount.trim()) {
      errors.amount = tSync('Amount is required')
    } else if (isNaN(Number(transactionForm.amount)) || Number(transactionForm.amount) === 0) {
      errors.amount = tSync('Amount must be a valid number')
    }
    if (!transactionForm.transactionDate.trim()) {
      errors.transactionDate = tSync('Transaction date is required')
    }
    
    setTransactionFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleCreateTransaction = async () => {
    if (!validateTransactionForm()) return
    
    try {
      const createdTransaction = await bankingApi.createBankTransaction({
        bankAccountId: transactionForm.bankAccountId,
        description: transactionForm.description.trim(),
        amount: Number(transactionForm.amount),
        transactionType: transactionForm.transactionType,
        transactionDate: new Date(transactionForm.transactionDate).toISOString(),
        merchantName: transactionForm.merchantName.trim() || undefined,
        category: transactionForm.category.trim() || undefined,
        reference: transactionForm.reference.trim() || undefined,
        memo: transactionForm.memo.trim() || undefined,
        status: 'unreconciled' as const
      })

      // Auto-reconcile if transaction matches existing bank statement transaction
      let reconciliationMessage = ""
      if (createdTransaction?.id && companyId) {
        try {
          const reconcileResult = await bankingApi.autoReconcileTransaction(createdTransaction.id, companyId)
          if (reconcileResult?.matched && reconcileResult?.reconciled) {
            reconciliationMessage = tSync("Transaction created and automatically reconciled with matching bank statement.")
          }
        } catch (reconcileError) {
          // Auto-reconciliation failed, but transaction was created successfully
          console.log('Auto-reconciliation attempt:', reconcileError)
        }
      }

      toast({
        title: tSync("Transaction Created"),
        description: reconciliationMessage || tSync("Bank transaction has been successfully created."),
      })

      // Reset form and close dialog
      setTransactionForm({
        bankAccountId: '',
        description: '',
        amount: '',
        transactionType: 'debit',
        transactionDate: new Date().toISOString().split('T')[0],
        merchantName: '',
        category: '',
        reference: '',
        memo: ''
      })
      setTransactionFormErrors({})
      setIsAddTransactionOpen(false)
      
      // Refresh transactions list
      setAccountsRefreshKey(prev => prev + 1)
      
    } catch (error) {
      console.error('Error creating transaction:', error)
      toast({
        title: tSync("Failed to Create Transaction"),
        description: tSync("An error occurred while creating the transaction."),
        variant: "destructive",
      })
      setTransactionFormErrors({ submit: tSync('Failed to create transaction. Please try again.') })
    }
  }

  if (isLoading) {
    return (
      <PageLayout 
        title="Banking & Cash Management" 
        description="Manage bank accounts, transactions, reconciliation, and advanced banking features"
        breadcrumbs={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Banking", href: "/dashboard/banking" }
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">
              <TranslatedText text="Loading authentication..." />
            </p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!isAuthenticated) {
    return (
      <PageLayout 
        title={tSync("Banking & Cash Management", "Banking & Cash Management")}
        description={tSync("Manage bank accounts, transactions, reconciliation, and advanced banking features", "Manage bank accounts, transactions, reconciliation, and advanced banking features")}
        breadcrumbs={[
          { label: tSync("Dashboard", "Dashboard"), href: "/dashboard" },
          { label: tSync("Banking", "Banking"), href: "/dashboard/banking" }
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-muted-foreground">
              <TranslatedText text="Please log in to access banking features." />
            </p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!mounted) {
    return (
      <PageLayout 
        title={tSync("Banking & Cash Management", "Banking & Cash Management")}
        description={tSync("Manage bank accounts, transactions, reconciliation, and advanced banking features", "Manage bank accounts, transactions, reconciliation, and advanced banking features")}
        breadcrumbs={[
          { label: tSync("Dashboard", "Dashboard"), href: "/dashboard" },
          { label: tSync("Banking", "Banking"), href: "/dashboard/banking" }
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">
              <TranslatedText text="Loading..." />
            </p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout 
      showBreadcrumbs={false}
    >
      <div className="space-y-3">
        {/* Breadcrumbs and Action Buttons in Same Row */}
        <div className="flex items-center justify-between">
        <div className="flex items-center">
          <BreadcrumbNavigation 
            customBreadcrumbs={[
              { label: tSync("Dashboard"), href: "/dashboard" },
              { label: tSync("Banking"), href: "/dashboard/banking" }
            ]}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setEditingBankAccount(null);
              setIsBankAccountFormOpen(true);
            }}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <TranslatedText text="Add Bank Account" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsUploadStatementOpen(true)}
            className="flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            <TranslatedText text="Upload Statement" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAccountsRefreshKey((k) => k + 1)}
          >
            <TranslatedText text="Refresh" />
          </Button>
        </div>
      </div>
      
      {/* Responsive Banking Layout */}
      <ResponsiveBankingLayout 
        companyId={companyId}
        accountsRefreshKey={accountsRefreshKey}
        setAccountsRefreshKey={setAccountsRefreshKey}
        isAddTransactionOpen={isAddTransactionOpen}
        setIsAddTransactionOpen={setIsAddTransactionOpen}
        transactionForm={transactionForm}
        setTransactionForm={setTransactionForm}
        transactionFormErrors={transactionFormErrors}
        setTransactionFormErrors={setTransactionFormErrors}
        handleTransactionFormChange={handleTransactionFormChange}
        handleCreateTransaction={handleCreateTransaction}
      />

      {/* Bank Statement Upload Dialog */}
      <BankStatementUpload
        open={isUploadStatementOpen}
        onOpenChange={setIsUploadStatementOpen}
        onSuccess={() => {
          setAccountsRefreshKey((k) => k + 1) // Refresh accounts and transactions
          // Transactions will auto-refresh via accountsRefreshKey dependency
        }}
        companyId={companyId}
      />

      {/* Bank Account Form Dialog */}
      <BankAccountForm
        open={isBankAccountFormOpen}
        onOpenChange={setIsBankAccountFormOpen}
        onSuccess={() => {
          setAccountsRefreshKey((k) => k + 1)
        }}
        companyId={companyId}
        editingAccount={editingBankAccount}
      />
      </div>
    </PageLayout>
  )
}
